import { useState, useEffect } from "react";
import AdminLayout from "@/components/AdminLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import api from "@/lib/api";
import {
  Plus, Edit, Trash2, Key, Globe, Package, Search,
  Loader2, X, CheckCircle, Eye, EyeOff, ChevronDown, ChevronUp
} from "lucide-react";

const EMPTY_CREDS = { admin: { username: "", password: "" }, user: { username: "", password: "" }, other: { username: "", password: "" } };

export default function AdminProductMaster() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [expandedCreds, setExpandedCreds] = useState(null);
  const [showPasswords, setShowPasswords] = useState({});
  const [form, setForm] = useState({
    name: "", demo_url: "", description: "", category: "", is_active: true,
    credentials: { ...EMPTY_CREDS }
  });

  useEffect(() => { loadProducts(); }, []);

  const loadProducts = async () => {
    setLoading(true);
    try { const r = await api.get("/presales/admin/products"); setProducts(r.data || []); }
    catch { toast.error("Failed to load products"); }
    setLoading(false);
  };

  const resetForm = () => {
    setForm({ name: "", demo_url: "", description: "", category: "", is_active: true, credentials: { admin: { username: "", password: "" }, user: { username: "", password: "" }, other: { username: "", password: "" } } });
    setEditing(null);
    setShowForm(false);
  };

  const startEdit = (p) => {
    setEditing(p);
    setForm({
      name: p.name || "", demo_url: p.demo_url || "", description: p.description || "",
      category: p.category || "", is_active: p.is_active !== false,
      credentials: p.credentials || { admin: { username: "", password: "" }, user: { username: "", password: "" }, other: { username: "", password: "" } }
    });
    setShowForm(true);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) { toast.error("Product name is required"); return; }
    setSaving(true);
    try {
      if (editing) {
        await api.put(`/presales/admin/products/${editing.id}`, form);
        toast.success("Product updated");
      } else {
        await api.post("/presales/admin/products", form);
        toast.success("Product created");
      }
      resetForm();
      loadProducts();
    } catch { toast.error("Failed to save product"); }
    setSaving(false);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this product?")) return;
    try { await api.delete(`/presales/admin/products/${id}`); toast.success("Product deleted"); loadProducts(); }
    catch { toast.error("Failed to delete"); }
  };

  const toggleActive = async (p) => {
    try {
      await api.put(`/presales/admin/products/${p.id}`, { is_active: !p.is_active });
      toast.success(p.is_active ? "Product deactivated" : "Product activated");
      loadProducts();
    } catch { toast.error("Failed to update"); }
  };

  const filtered = products.filter(p => {
    const q = search.toLowerCase();
    return !q || (p.name || "").toLowerCase().includes(q) || (p.category || "").toLowerCase().includes(q) || (p.description || "").toLowerCase().includes(q);
  });

  const togglePasswordVisibility = (productId, role) => {
    setShowPasswords(prev => ({ ...prev, [`${productId}-${role}`]: !prev[`${productId}-${role}`] }));
  };

  return (
    <AdminLayout title="Product Master">
      <div className="space-y-5">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-slate-900" data-testid="admin-products-title">Product Master</h1>
            <p className="text-sm text-slate-500 mt-0.5">Manage demo products, URLs, and credentials for prospects</p>
          </div>
          <Button onClick={() => { resetForm(); setShowForm(true); }} className="bg-[#0066CC] hover:bg-[#0052A3] gap-1.5" data-testid="add-product-btn">
            <Plus className="w-4 h-4" /> Add Product
          </Button>
        </div>

        {/* Create/Edit Form */}
        {showForm && (
          <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6" data-testid="product-form">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-slate-900">{editing ? "Edit Product" : "New Product"}</h2>
              <button onClick={resetForm} className="p-1 hover:bg-slate-100 rounded-lg"><X className="w-5 h-5 text-slate-400" /></button>
            </div>
            <form onSubmit={handleSave} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label className="text-sm font-medium">Product Name *</Label>
                  <Input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} placeholder="e.g., SAP Business One" className="mt-1" required data-testid="product-name-input" />
                </div>
                <div>
                  <Label className="text-sm font-medium">Demo URL</Label>
                  <Input value={form.demo_url} onChange={e => setForm({ ...form, demo_url: e.target.value })} placeholder="https://demo.product.com" className="mt-1" data-testid="product-url-input" />
                </div>
                <div>
                  <Label className="text-sm font-medium">Category</Label>
                  <Input value={form.category} onChange={e => setForm({ ...form, category: e.target.value })} placeholder="e.g., ERP, CRM, Analytics" className="mt-1" data-testid="product-category-input" />
                </div>
                <div>
                  <Label className="text-sm font-medium">Status</Label>
                  <div className="flex items-center gap-3 mt-2.5">
                    <button type="button" onClick={() => setForm({ ...form, is_active: !form.is_active })}
                      className={`relative w-11 h-6 rounded-full transition-colors ${form.is_active ? "bg-emerald-500" : "bg-slate-300"}`}>
                      <span className={`absolute top-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${form.is_active ? "left-[22px]" : "left-0.5"}`} />
                    </button>
                    <span className="text-sm text-slate-600">{form.is_active ? "Active" : "Inactive"}</span>
                  </div>
                </div>
              </div>
              <div>
                <Label className="text-sm font-medium">Description</Label>
                <Textarea value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} placeholder="Brief description of the product..." rows={2} className="mt-1" />
              </div>

              {/* 3 Credential Sets */}
              <div>
                <Label className="text-sm font-medium mb-2 block">Demo Credentials (3 Sets)</Label>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {["admin", "user", "other"].map(role => (
                    <div key={role} className="border border-slate-200 rounded-xl p-4 bg-slate-50/50">
                      <div className="flex items-center gap-2 mb-3">
                        <Key className="w-4 h-4 text-[#0066CC]" />
                        <span className="text-sm font-semibold text-slate-700 capitalize">{role} Credentials</span>
                      </div>
                      <div className="space-y-2">
                        <Input
                          value={form.credentials?.[role]?.username || ""}
                          onChange={e => setForm({ ...form, credentials: { ...form.credentials, [role]: { ...form.credentials?.[role], username: e.target.value } } })}
                          placeholder="Username" className="text-sm h-9" data-testid={`cred-${role}-user`} />
                        <Input
                          value={form.credentials?.[role]?.password || ""}
                          onChange={e => setForm({ ...form, credentials: { ...form.credentials, [role]: { ...form.credentials?.[role], password: e.target.value } } })}
                          placeholder="Password" className="text-sm h-9" data-testid={`cred-${role}-pass`} />
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="flex gap-2 pt-2 border-t border-slate-100">
                <Button type="submit" disabled={saving} className="bg-[#0066CC] hover:bg-[#0052A3] gap-1" data-testid="save-product-btn">
                  {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle className="w-4 h-4" />}
                  {editing ? "Update Product" : "Create Product"}
                </Button>
                <Button type="button" variant="outline" onClick={resetForm}>Cancel</Button>
              </div>
            </form>
          </div>
        )}

        {/* Search */}
        <div className="flex items-center gap-3">
          <div className="relative flex-1 max-w-sm">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search products..." className="pl-9" data-testid="product-search" />
          </div>
          <span className="text-sm text-slate-400">{filtered.length} product{filtered.length !== 1 ? "s" : ""}</span>
        </div>

        {/* Products List */}
        {loading ? (
          <div className="flex justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-slate-300" /></div>
        ) : filtered.length === 0 ? (
          <div className="bg-white rounded-2xl border border-slate-200 shadow-sm text-center py-16">
            <Package className="w-14 h-14 text-slate-200 mx-auto mb-3" />
            <p className="text-slate-400 text-sm">{search ? "No products match your search" : "No products yet. Add your first product above."}</p>
          </div>
        ) : (
          <div className="space-y-3">
            {filtered.map(p => (
              <div key={p.id} className={`bg-white rounded-2xl border shadow-sm transition-all hover:shadow-md ${p.is_active === false ? "border-slate-200 opacity-60" : "border-slate-200"}`} data-testid={`product-row-${p.id}`}>
                <div className="flex items-center justify-between p-4">
                  <div className="flex items-center gap-4 flex-1 min-w-0">
                    <div className={`w-10 h-10 rounded-xl flex items-center justify-center text-sm font-bold shrink-0 ${p.is_active === false ? "bg-slate-100 text-slate-400" : "bg-[#0066CC]/10 text-[#0066CC]"}`}>
                      {(p.name || "P")[0].toUpperCase()}
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <p className="font-semibold text-slate-900 truncate">{p.name}</p>
                        {p.category && <span className="px-2 py-0.5 bg-blue-50 text-blue-600 rounded text-xs font-medium">{p.category}</span>}
                        <span className={`px-2 py-0.5 rounded text-xs font-medium ${p.is_active === false ? "bg-slate-100 text-slate-500" : "bg-emerald-50 text-emerald-600"}`}>
                          {p.is_active === false ? "Inactive" : "Active"}
                        </span>
                      </div>
                      {p.demo_url && <p className="text-xs text-blue-500 truncate mt-0.5 flex items-center gap-1"><Globe className="w-3 h-3 shrink-0" />{p.demo_url}</p>}
                      {p.description && <p className="text-xs text-slate-400 mt-0.5 truncate">{p.description}</p>}
                    </div>
                  </div>
                  <div className="flex items-center gap-2 ml-4">
                    <button onClick={() => setExpandedCreds(expandedCreds === p.id ? null : p.id)}
                      className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs font-medium text-slate-500 hover:bg-slate-100 transition-colors" data-testid={`toggle-creds-${p.id}`}>
                      <Key className="w-3.5 h-3.5" />
                      {expandedCreds === p.id ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
                    </button>
                    <button onClick={() => toggleActive(p)}
                      className={`relative w-9 h-5 rounded-full transition-colors ${p.is_active === false ? "bg-slate-300" : "bg-emerald-500"}`}>
                      <span className={`absolute top-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${p.is_active === false ? "left-0.5" : "left-[18px]"}`} />
                    </button>
                    <button onClick={() => startEdit(p)} className="p-1.5 hover:bg-blue-50 rounded-lg transition-colors" data-testid={`edit-product-${p.id}`}>
                      <Edit className="w-4 h-4 text-slate-400 hover:text-[#0066CC]" />
                    </button>
                    <button onClick={() => handleDelete(p.id)} className="p-1.5 hover:bg-red-50 rounded-lg transition-colors" data-testid={`delete-product-${p.id}`}>
                      <Trash2 className="w-4 h-4 text-slate-400 hover:text-red-500" />
                    </button>
                  </div>
                </div>

                {/* Expanded Credentials */}
                {expandedCreds === p.id && p.credentials && (
                  <div className="border-t border-slate-100 p-4 bg-slate-50/50">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                      {["admin", "user", "other"].map(role => {
                        const cred = p.credentials[role];
                        const hasData = cred?.username;
                        const isVisible = showPasswords[`${p.id}-${role}`];
                        return (
                          <div key={role} className={`rounded-xl p-3 border ${hasData ? "bg-white border-slate-200" : "bg-slate-50 border-slate-100"}`}>
                            <div className="flex items-center justify-between mb-2">
                              <span className="text-xs font-bold text-slate-500 uppercase tracking-wider">{role}</span>
                              {hasData && (
                                <button onClick={() => togglePasswordVisibility(p.id, role)} className="p-1 hover:bg-slate-100 rounded">
                                  {isVisible ? <EyeOff className="w-3 h-3 text-slate-400" /> : <Eye className="w-3 h-3 text-slate-400" />}
                                </button>
                              )}
                            </div>
                            {hasData ? (
                              <div className="space-y-1">
                                <p className="text-xs"><span className="text-slate-400 w-10 inline-block">User:</span> <span className="font-medium text-slate-700">{cred.username}</span></p>
                                <p className="text-xs"><span className="text-slate-400 w-10 inline-block">Pass:</span> <span className="font-medium text-slate-700">{isVisible ? cred.password : "••••••"}</span></p>
                              </div>
                            ) : (
                              <p className="text-xs text-slate-300 italic">Not configured</p>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </AdminLayout>
  );
}
